import React, { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { Building2, TrendingUp, FileStack } from "lucide-react";

export default function Dashboard() {
  const [campuses, setCampuses] = useState([]);
  const [campus, setCampus] = useState("");
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [d, setD] = useState(null);

  const load = () => {
    const params = {};
    if (campus) params.campus_id = campus;
    if (frm) params.frm = frm;
    if (to) params.to = to;
    api.get("/reports/dashboard", { params }).then(({ data }) => setD(data));
  };
  useEffect(() => { api.get("/campuses").then(({ data }) => setCampuses(data)); }, []);
  useEffect(load, [campus, frm, to]); // eslint-disable-line

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl">Dashboard</h1>
          <p className="text-[color:var(--ink-2)]">Locked count totals across campuses.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select className="field h-11 w-auto" value={campus} onChange={(e) => setCampus(e.target.value)} data-testid="dash-campus">
            <option value="">All Campuses</option>
            {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="date" className="field h-11 w-auto" value={frm} onChange={(e) => setFrm(e.target.value)} data-testid="dash-from" />
          <input type="date" className="field h-11 w-auto" value={to} onChange={(e) => setTo(e.target.value)} data-testid="dash-to" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KPI icon={TrendingUp} label="Grand Total" value={money(d?.grand_total)} />
        <KPI icon={FileStack} label="Sessions" value={d?.session_count ?? 0} />
        <KPI icon={Building2} label="Campuses" value={d?.by_campus?.length ?? 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="font-semibold mb-4">Totals by Campus</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d?.by_campus || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DCE0D4" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="total" fill="#4A5D23" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card p-6">
          <h3 className="font-semibold mb-4">Trend</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={d?.trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DCE0D4" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => money(v)} />
              <Line type="monotone" dataKey="total" stroke="#E2725B" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SplitTable title="By Fund" rows={d?.by_fund} />
        <SplitTable title="By Service" rows={d?.by_service} />
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="card p-6 flex items-center gap-4" data-testid={`kpi-${label.split(" ")[0].toLowerCase()}`}>
      <div className="w-12 h-12 rounded-xl bg-[color:var(--olive-light)] flex items-center justify-center"><Icon className="text-[color:var(--olive)]" /></div>
      <div><div className="text-xs uppercase tracking-wider text-[color:var(--muted)]">{label}</div><div className="font-mono tabnum text-2xl font-semibold">{value}</div></div>
    </div>
  );
}
function SplitTable({ title, rows }) {
  return (
    <div className="card p-6">
      <h3 className="font-semibold mb-3">{title}</h3>
      {(!rows || rows.length === 0) && <p className="text-sm text-[color:var(--muted)]">No data.</p>}
      <div className="space-y-2">
        {(rows || []).map((r) => (
          <div key={r.name} className="flex justify-between text-sm py-2 border-b border-[color:var(--line)] last:border-0">
            <span>{r.name}</span><span className="font-mono tabnum font-semibold">{money(r.total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
